// Rune ids that used to be spelled differently.
//
// The language is allowed to rename a rune; what it is not allowed to do is
// quietly forget one a player already learned. Every id here appears in things
// written down outside this repository -- a browser's `learned-runes` key, a
// save file, a room JSON sitting in the draft table, a recorded take on
// somebody's disk -- and every one of those is read back by id.
//
// **`large` → `unbound`, 2026-08-14.** The modifier has displayed as *Unbound*
// since 2026-07-28; only the id stayed `large`, on the reasoning that a display
// name was the whole of what changed. It was not: an id is not private. It is
// what a room's `puzzle.runes` lists, what `interact.giveRuneId` grants, what a
// recording stores, and -- since the Puzzle Analysis rune picker started drawing
// the language as a list of checkboxes -- what a designer reads off the screen.
// Two names for one rune is one name too many, so the id moved to match the name
// rather than the name being explained away.
//
// Applied at every point where an id crosses back in from storage, which is the
// only place it can be stale: `loadRoom` (authored and draft rooms),
// `worldFromRecording` (takes) and `game/`'s learned-rune persistence. Not
// applied anywhere in the running simulation -- inside the process there is one
// spelling and it is the current one.
const RENAMED: Record<string, string> = { large: 'unbound' };

/** The current id for whatever `id` used to be called. Unknown ids pass through
 * untouched: this is a rename table, not a whitelist, and refusing an id it has
 * never heard of would make every future rune a migration. */
export function currentRuneId(id: string): string {
  return RENAMED[id] ?? id;
}

/** `currentRuneId` over a list, order preserved. */
export function currentRuneIds(ids: readonly string[]): string[] {
  return ids.map(currentRuneId);
}

/** Whether any id in the list is one of the old spellings — for a caller that
 * wants to say so rather than migrate silently. */
export function hasLegacyRuneId(ids: readonly string[]): boolean {
  return ids.some((id) => id in RENAMED);
}
